// @flow
import React, { Fragment, useCallback, useMemo, useEffect, SyntheticEvent } from 'react';
import { assoc, isEmpty } from 'ramda';

import PlainInput from 'app/common/ui-next/plain-input';
import PlainLabel from 'app/common/ui-next/plain-label';
import { PlainItemsSelector } from 'app/common/ui-next/plain-selector';
import PlainHelperText from 'app/common/ui-next/plain-helper-text';

import {
  useMachineForm,
  useMachineValidate,
  useAvailableOrgs,
} from 'app/dataProviders/VendingDataProvider';

import useActualModels from './useActualModels';
import useActualVersions from './useActualVersions';
import { useActualBuildings } from './useActualBuildings';
import { useActualSalePoints } from './useActualSalePoints';


const emptySelection = [];

type Props = {
  disabled: boolean,
}

const AddMachine = ({ disabled }: Props) => {
  const [{ title, number, orgId, buildingId }, { update }] = useMachineForm();
  const [errors, , resetErrors] = useMachineValidate();


  const [availableOrgs, loadingOrgs, requestOrgs] = useAvailableOrgs();
  useEffect(() => { requestOrgs(); }, [requestOrgs]);

  const orgSelection = useMemo(() => (orgId ? [String(orgId)] : []), [orgId]);

  const changeCurrentOrg = useCallback(([orgId_]: Array<string>) => {
    resetErrors();
    update(assoc('orgId', parseInt(orgId_, 10) || 0));
  }, [update, resetErrors]);


  const {
    models,
    loadingModels,
    modelSelection,
    changeCurrentModel,
  } = useActualModels();

  const modelNotYetSelected = isEmpty(modelSelection);

  const {
    versions,
    loadingVersions,
    versionSelection,
    changeCurrentVersion,
  } = useActualVersions(modelSelection);

  const {
    buildings,
    loadingBuildings,
    buildingSelection,
    changeCurrentBuilding,
  } = useActualBuildings();

  const {
    salePoints,
    loadingSalePoints,
    salePointSelection,
    changeCurrentSalePoint,
  } = useActualSalePoints()


  const changeInputField = useCallback((e: SyntheticEvent<HTMLInputElement>) => {
    const { value, id } = e.currentTarget;
    if (id === 'title' || id === 'number') {
      resetErrors();
      update(assoc(id, value));
    }
  }, [update, resetErrors]);


  useEffect(() => () => {
    resetErrors();
  }, [resetErrors]);


  return (
    <Fragment>
      <PlainItemsSelector
        label="Производитель"
        header="Выберите производителя"
        pleaseSelectText="Выберите производителя"
        items={models}
        selection={modelSelection}
        loading={loadingModels}
        defaultSelection={emptySelection}
        onChange={changeCurrentModel}
        selectedAllOnEmptySelection={false}
        disabled={disabled}
      />
      <PlainHelperText error>
        {errors.modelId}
      </PlainHelperText>

      <PlainItemsSelector
        label="Модель аппарата"
        header="Выберите модель аппарата"
        pleaseSelectText="Выберите модель аппарата"
        items={versions}
        selection={versionSelection}
        loading={loadingVersions}
        defaultSelection={emptySelection}
        onChange={changeCurrentVersion}
        selectedAllOnEmptySelection={false}
        disabled={disabled || modelNotYetSelected}
      />
      <PlainHelperText error>
        {errors.versionId}
      </PlainHelperText>

      <PlainLabel htmlFor="title" displayAsBlock>
        {'Наименование аппарата'}
      </PlainLabel>
      <PlainInput
        id="title"
        value={title}
        onChange={changeInputField}
        disabled={disabled}
        error={!!errors.title}
        displayAsBlock
      />
      <PlainHelperText error>
        {errors.title}
      </PlainHelperText>

      <PlainLabel htmlFor="number" displayAsBlock>
        {'Номер аппарата'}
      </PlainLabel>
      <PlainInput
        id="number"
        value={number}
        onChange={changeInputField}
        disabled={disabled}
        error={!!errors.number}
        displayAsBlock
      />
      <PlainHelperText error>
        {errors.number}
      </PlainHelperText>

      <PlainItemsSelector
        label="Место размещения"
        header="Выберите заведение, где стоит аппарат"
        pleaseSelectText="Выберите заведение"
        items={availableOrgs}
        selection={orgSelection}
        loading={loadingOrgs}
        defaultSelection={emptySelection}
        onChange={changeCurrentOrg}
        selectedAllOnEmptySelection={false}
        disabled={disabled}
      />
      <PlainHelperText error>
        {errors.orgId}
      </PlainHelperText>

      <PlainItemsSelector
        label="Корпус"
        header="Выберите корпус, где стоит аппарат"
        pleaseSelectText="Выберите корпус"
        items={buildings}
        selection={buildingSelection}
        loading={loadingBuildings}
        defaultSelection={emptySelection}
        onChange={changeCurrentBuilding}
        selectedAllOnEmptySelection={false}
        disabled={disabled || !orgId || loadingBuildings}
      />
      <PlainHelperText error>
        {errors.buildingId}
      </PlainHelperText>

      <PlainItemsSelector
        label="Точка продаж"
        header="Выберите точку продаж, где стоит аппарат"
        pleaseSelectText="Выберите точку продаж"
        items={salePoints}
        selection={salePointSelection}
        loading={loadingSalePoints}
        defaultSelection={emptySelection}
        onChange={changeCurrentSalePoint}
        selectedAllOnEmptySelection={false}
        disabled={disabled || !orgId || !buildingId || loadingSalePoints}
      />
      <PlainHelperText error>
        {errors.salePointId}
      </PlainHelperText>
    </Fragment>
  );
};

export default AddMachine;
